import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, fmt } from "../../../../services/erpApi";
import { useToast } from "../../../../components/admin/ToastContext";
import { LoadingScreen } from "../../../../components/admin/EssUi";
import CustomerInvoiceView from "./components/CustomerInvoiceView";
import StageBar, { INVOICE_STAGES } from "../../../../components/admin/StageBar";
import { Plus, Trash2 } from "lucide-react";
import CustomerSelect from "../../../../components/admin/CustomerSelect";
import { generateInvoicePdf, downloadPdfBlob } from "../../../../utils/crmReportPdf";

const newInvoiceForm = () => ({
  partner_id: "",
  date: new Date().toISOString().split("T")[0],
  due_date: "",
  journal_id: "",
  ref: "",
  code_fournisseur: "",
  billing_period: "",
  maturity_date: "",
  early_payment_request_sent: false,
  early_payment_request_date: "",
  buyer: "",
  delivery_address: "",
  sale_order: "",
  payment_reference: "",
  project_budget: "",
  currency: "CFA",
  lines: [],
});

const invStyles = {
  page: { minHeight: "100vh", background: "#f6f7f8", color: "#263746", fontFamily: '"Lucida Grande", Helvetica, Verdana, Arial, sans-serif' },
  topbar: { minHeight: 31, display: "flex", alignItems: "center", justifyContent: "space-between", padding: "0 14px", background: "#fff", borderBottom: "1px solid #d8dde2", fontSize: 13 },
  breadcrumb: { display: "flex", alignItems: "center", gap: 8, color: "#63758a" },
  back: { display: "inline-flex", alignItems: "center", gap: 5, border: 0, background: "transparent", color: "#63758a", cursor: "pointer", padding: 0 },
  newButton: { display: "inline-flex", gap: 5, alignItems: "center", border: 0, background: "#6594ad", color: "#fff", padding: "7px 12px", fontSize: 11, cursor: "pointer" },
  formPage: { minHeight: "calc(100vh - 58px)", padding: "0 12px 30px" },
  formToolbar: { display: "flex", alignItems: "center", gap: 12, minHeight: 48, fontSize: 11 },
  confirm: { border: 0, background: "#6594ad", color: "#fff", padding: "8px 13px", cursor: "pointer", fontSize: 11 },
  cancel: { border: 0, background: "transparent", color: "#714b67", cursor: "pointer", fontSize: 11 },
  statusSteps: { marginLeft: "auto", display: "flex", gap: 0, color: "#97a0a8", fontSize: 10 },
  statusStep: { padding: "8px 16px", borderLeft: "1px solid #d8dde2", background: "#fff" },
  statusStepActive: { padding: "8px 16px", background: "#263d4a", color: "#fff" },
  sheet: { width: "min(100%, 1008px)", margin: "0 auto", padding: "30px 24px 36px", boxSizing: "border-box", background: "#fff", border: "1px solid #e0e4e7", boxShadow: "0 1px 3px rgba(0,0,0,.04)" },
  sheetTitle: { margin: "0 0 18px", fontSize: 20, fontWeight: 600, color: "#263746" },
  fields: { display: "grid", gridTemplateColumns: "1fr 1fr", gap: "4px 48px" },
  field: { display: "grid", gridTemplateColumns: "115px 1fr", alignItems: "center", minHeight: 34, fontSize: 11, color: "#526170" },
  readonly: { borderBottom: "1px solid #aeb8c1", padding: "7px 4px", color: "#34495a", minHeight: 14 },
  input: { width: "100%", boxSizing: "border-box", border: 0, borderBottom: "1px solid #aeb8c1", padding: "7px 4px", outline: 0, color: "#34495a", background: "transparent", fontSize: 12 },
  tabs: { display: "flex", gap: 18, borderBottom: "1px solid #dce1e5", marginTop: 28, paddingBottom: 8, color: "#6c7b88", fontSize: 11 },
  table: { overflowX: "auto", marginTop: 0 },
  tableHead: { display: "grid", gridTemplateColumns: "1.4fr 1.6fr .6fr .9fr .7fr 28px", gap: 7, alignItems: "center", padding: "8px 6px", background: "#e9edf1", color: "#405161", fontSize: 10, fontWeight: 600, minWidth: 720 },
  tableRow: { display: "grid", gridTemplateColumns: "1.4fr 1.6fr .6fr .9fr .7fr 28px", gap: 7, alignItems: "center", padding: "5px 6px", borderBottom: "1px solid #edf0f2", minWidth: 720, color: "#405161", fontSize: 11 },
  cellInput: { width: "100%", boxSizing: "border-box", border: 0, borderBottom: "1px solid #c6cdd3", padding: "6px 3px", outline: 0, fontSize: 11, color: "#405161" },
  delete: { border: 0, background: "transparent", color: "#be123c", cursor: "pointer" },
  total: { display: "flex", justifyContent: "flex-end", gap: 18, borderTop: "1px solid #dce1e5", paddingTop: 14, marginTop: 12, fontSize: 12 },
  chatter: { width: "min(100%, 1040px)", margin: "12px auto 0", background: "#fff", borderTop: "1px solid #dce1e5", color: "#526170" },
};

function InvField({ label, children }) { return <label style={invStyles.field}><span>{label}</span>{children}</label>; }

function InvoiceForm({ form, setForm, partners, products, journals, subtotal, addLine, updateLine, removeLine, chooseProduct, creating, onCreate, onCancel, onStageSelect }) {
  // reproduction exacte de CustomerInvoiceView mais avec inputs au lieu de readonly — comme QuotationForm
  const currentStage = "draft";
  return (
    <div style={invStyles.formPage}>
      <div style={invStyles.formToolbar}><button type="button" onClick={onCreate} disabled={creating || !form.partner_id || !form.lines.some((l) => l.product_id)} style={invStyles.confirm}>{creating ? "CREATING..." : "CONFIRM"}</button><button type="button" onClick={onCancel} style={invStyles.cancel}>DISCARD</button><div style={{ flex: "1 1 auto", maxWidth: 420, marginLeft: "auto" }}><StageBar stages={INVOICE_STAGES} currentStageId={currentStage} onSelect={onStageSelect} /></div></div>
      <div style={invStyles.sheet}><h1 style={invStyles.sheetTitle}>New</h1>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "8px 32px" }}>
          <div>
            <InvField label="Customer *">
              <CustomerSelect value={form.partner_id} onChange={(id) => setForm((c) => ({ ...c, partner_id: id }))} placeholder="Search customer… (tape 2 lettres)" />
            </InvField>
            <InvField label="Invoice Date *"><input type="date" value={form.date} onChange={(e) => setForm((c) => ({ ...c, date: e.target.value }))} style={invStyles.input} /></InvField>
          </div>
          <div>
            <InvField label="Due Date"><input type="date" value={form.due_date} onChange={(e) => setForm((c) => ({ ...c, due_date: e.target.value }))} style={invStyles.input} /></InvField>
            <InvField label="Journal">
              <select value={form.journal_id} onChange={(e) => setForm((c) => ({ ...c, journal_id: e.target.value }))} style={invStyles.input}>
                <option value="">Customer Invoices (default)</option>
                {journals.map((j) => <option key={j.id} value={j.id}>{j.name || j.code} — {j.currency || "CFA"}</option>)}
              </select>
            </InvField>
          </div>
        </div>
        <div style={{ fontSize: 11, color: "#6c7b88", marginTop: 6 }}>Seuls Customer / Dates / Journal / Lignes sont persistés.</div>
        <div style={invStyles.tabs}><strong>Invoice Lines *</strong></div>
        <div style={invStyles.table}><div style={invStyles.tableHead}><span>Product</span><span>Label</span><span>Quantity</span><span>Unit Price</span><span>Subtotal</span><span /></div>
          {form.lines.map((line, index) => (
            <div style={invStyles.tableRow} key={index}>
              <select value={line.product_id} onChange={(e) => chooseProduct(index, e.target.value)} style={invStyles.cellInput}><option value="">Add a product</option>{products.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}</select>
              <input value={line.name || ""} onChange={(e) => updateLine(index, { name: e.target.value })} placeholder="Description" style={invStyles.cellInput} />
              <input type="number" min="1" value={line.qty} onChange={(e) => updateLine(index, { qty: e.target.value })} style={invStyles.cellInput} />
              <input type="number" min="0" value={line.price_unit} onChange={(e) => updateLine(index, { price_unit: e.target.value })} style={invStyles.cellInput} />
              <strong>{fmt.money(Number(line.qty || 0) * Number(line.price_unit || 0))}</strong>
              <button type="button" onClick={() => removeLine(index)} style={invStyles.delete}><Trash2 size={13} /></button>
            </div>
          ))}
          <div style={{ padding: "8px 6px", display: "flex", gap: 12 }}>
            <button type="button" onClick={addLine} style={{ border: 0, background: "transparent", color: "#714B67", cursor: "pointer", fontSize: 11 }}><Plus size={12} /> Add a product</button>
            <span style={{ color: "#6f668d", fontSize: 11 }}>Add a section — Add a note</span>
          </div>
        </div>
        <div style={invStyles.total}><span>Untaxed: {fmt.money(subtotal)}</span><span>Tax: {fmt.money(Math.round(subtotal * 0.1925))}</span><strong>Total: {fmt.money(subtotal + Math.round(subtotal * 0.1925))}</strong></div>
        <div style={{ marginTop: 18, fontSize: 11, color: "#6c7b88" }}>Terms & Conditions: <span style={{ color: "#16869b" }}>https://africasystems.com/terms</span></div>
      </div>
    </div>
  );
}

const mapInvoiceToView = (row, lines) => {
  // Backend peut renvoyer state null mais posted=true / payment_state=posted / status=posted selon endpoint (sales/invoices vs account-moves)
  const rawState = row.state ?? row.status ?? row.move_state ?? null;
  const isPaidRaw = row.payment_state === "paid" || row.payment_state === "in_payment" || rawState === "paid";
  const isPostedRaw = rawState === "posted" || row.posted === true || row.is_posted === true || row.payment_state === "posted" || (!isPaidRaw && rawState == null && row.amount_residual != null && Number(row.amount_residual) < Number(row.amount_total));
  const normalizedState = isPaidRaw ? "paid" : isPostedRaw ? "posted" : "draft";
  const status = normalizedState === "paid" ? "paid" : normalizedState === "posted" ? "posted" : "draft";
  const invoiceDate = row.date || row.invoice_date ? fmt.date(row.date || row.invoice_date) : "";
  const dueDate = row.date_due ? fmt.date(row.date_due) : row.due_date ? fmt.date(row.due_date) : "";
  return {
    number: row.name || row.number || `INV${row.id}`,
    status,
    state: row.state,
    payment_state: row.payment_state,
    customer: row.partner_name || `Partner #${row.partner_id || "—"}`,
    refPurchaseOrder: row.ref || row.partner_ref || "",
    codeFournisseur: row.code_fournisseur || "",
    billingPeriod: row.billing_period || "",
    maturityDate: row.maturity_date ? fmt.date(row.maturity_date) : "",
    earlyPaymentRequestSent: !!row.early_payment_request_sent,
    earlyPaymentRequestDate: row.early_payment_request_date ? fmt.date(row.early_payment_request_date) : "",
    buyer: row.buyer_name || row.user_name || "—",
    deliveryAddress: row.partner_name || row.delivery_address || row.partner_name || "—",
    invoiceDate,
    saleOrder: row.sale_order_name || (row.sale_order_id ? `SO#${row.sale_order_id}` : ""),
    paymentReference: row.payment_reference || row.name || "",
    projectBudget: row.project_budget || "",
    dueDate,
    journal: row.journal_name || row.journal || "Customer Invoices",
    currency: row.currency || "CFA",
    termsUrl: "https://africasystems.com/terms",
    feeApplied: Number(row.fee_applied || 0),
    raw: row,
    lines: (lines || row.lines || []).map((l) => ({
      product: l.product_name || l.name || "—",
      label: l.name || l.product_name || "—",
      account: l.account_name || l.account || "701100 Vente de merchandises dans la Région",
      qty: Number(l.quantity ?? l.qty ?? l.product_uom_qty ?? 0),
      uom: l.uom_name || "Units",
      price: Number(l.price_unit ?? l.price ?? 0),
      tax: l.tax_name || l.tax || "TVA 19.25% (vente)",
      subtotal: Number(l.price_subtotal ?? Number(l.price_unit || 0) * Number(l.quantity ?? l.qty ?? l.product_uom_qty ?? 0)),
    })),
    history: row.history || [
      {
        date: row.create_date ? fmt.date(row.create_date) : new Date().toLocaleDateString("en-GB", { month: "long", day: "numeric", year: "numeric" }),
        entries: [
          { author: row.create_uid_name || row.user_name || "System", time: "now", note: row.state === "posted" ? "Invoice validated" : "Invoice Created" },
        ],
      },
    ],
  };
};

const CustomerInvoiceDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const toast = useToast();
  const isNew = id === "new";
  const [invoice, setInvoice] = useState(null);
  const [lines, setLines] = useState([]);
  const [loading, setLoading] = useState(!isNew);
  // new invoice form state — inspiré de CrmV2QuotationPage.js newForm/QuotationForm
  const [form, setForm] = useState(() => newInvoiceForm());
  const [partners, setPartners] = useState([]);
  const [products, setProducts] = useState([]);
  const [journals, setJournals] = useState([]);
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState(false);
  const [saleOrders, setSaleOrders] = useState([]);
  const subtotal = useMemo(() => form.lines.reduce((s, l) => s + (Number(l.qty) || 0) * (Number(l.price_unit) || 0), 0), [form.lines]);
  // Credit Note modal state
  const [creditOpen, setCreditOpen] = useState(false);
  const [creditBusy, setCreditBusy] = useState(false);
  const [creditForm, setCreditForm] = useState({ refund_method: "refund", reason: "", journal_id: "", date_mode: "entry", date: new Date().toISOString().slice(0,10) });

  const hydrateFormFromInvoice = useCallback(() => {
    if (!invoice) return;
    setForm({
      partner_id: invoice.partner_id || "",
      date: (invoice.date || invoice.invoice_date || "").slice(0, 10),
      due_date: (invoice.date_due || invoice.due_date || "").slice(0, 10),
      journal_id: invoice.journal_id || "",
      ref: invoice.ref || invoice.partner_ref || "",
      code_fournisseur: invoice.code_fournisseur || "",
      billing_period: invoice.billing_period || "",
      maturity_date: (invoice.maturity_date || "").slice(0, 10),
      early_payment_request_sent: !!invoice.early_payment_request_sent,
      early_payment_request_date: (invoice.early_payment_request_date || "").slice(0, 10),
      buyer: invoice.buyer || invoice.buyer_name || "",
      delivery_address: invoice.delivery_address || "",
      sale_order: invoice.sale_order || invoice.sale_order_name || "",
      payment_reference: invoice.payment_reference || invoice.name || "",
      project_budget: invoice.project_budget || "",
      currency: invoice.currency || "CFA",
      salesperson: invoice.salesperson || "",
      sales_team: invoice.sales_team || "AFS GLOBAL",
      recipient_bank: invoice.recipient_bank || "40007300011 - BGFI BANK",
      incoterm: invoice.incoterm || "",
      fiscal_position: invoice.fiscal_position || "",
      auto_post: invoice.auto_post || "no",
      to_check: !!invoice.to_check,
      lines: lines.map((l) => ({
        product_id: l.product_id || "",
        name: l.name || l.product_name || "",
        qty: l.quantity ?? l.qty ?? l.product_uom_qty ?? 1,
        price_unit: l.price_unit ?? l.price ?? 0,
      })),
    });
  }, [invoice, lines]);

  const load = useCallback(async () => {
    if (isNew) return;
    setLoading(true);
    try {
      let detail = null;
      try { detail = await api.accounting.get(id); } catch {}
      if (!detail) {
        const rows = await api.sales.invoices({ limit: 500 }).catch(() => []);
        detail = (Array.isArray(rows) ? rows : []).find((r) => String(r.id) === String(id) || String(r.lewoo_uid) === String(id)) || null;
      }
      if (!detail) throw new Error("Invoice not found");
      setInvoice(detail);
      let lineRows = detail.lines || detail.invoice_lines || [];
      if (!lineRows.length && detail.id) {
        try {
          const fetched = await api.accounting.get(detail.lewoo_uid || detail.id);
          lineRows = fetched?.lines || fetched?.invoice_lines || [];
        } catch {}
      }
      setLines(Array.isArray(lineRows) ? lineRows : []);
      // résoudre saleOrders liés — utilisé par SmartButton Odoo
      try {
        const origin = detail.sale_order_name || detail.origin || detail.invoice_origin || "";
        const soId = detail.sale_order_id || detail.sale_order_ids?.[0];
        let soRows = [];
        if (soId) {
          try { const one = await api.sales.get(soId); if (one) soRows = [one]; } catch {}
        }
        if (!soRows.length && origin) {
          const rows = await api.sales.list({ search: origin, limit: 5 }).catch(() => []);
          const arr = Array.isArray(rows) ? rows : [];
          soRows = arr.filter((o) => o.name === origin || String(o.id) === String(origin));
          if (!soRows.length) soRows = arr.slice(0, 1);
        }
        if (!soRows.length && detail.partner_id) {
          // fallback partner — dernier SO du client
          const rows = await api.sales.list({ search: detail.partner_name || "", limit: 5 }).catch(() => []);
          const arr = Array.isArray(rows) ? rows : [];
          soRows = arr.filter((o) => String(o.partner_id) === String(detail.partner_id)).slice(0, 1);
        }
        setSaleOrders(soRows);
      } catch { setSaleOrders([]); }
    } catch (e) {
      toast(e.message || "Load failed", "error");
    }
    setLoading(false);
  }, [id, toast, isNew]);

  useEffect(() => { load(); }, [load]);

  // charger référentiels aussi en mode détail pour l'édition — liste clients vide fix: ne filtre pas customer_rank pour factures, fallback sans filtre
  useEffect(() => {
    let active = true;
    (async () => {
      const fetchPartners = async () => {
        // 1) tente customers strict
        let rows = await api.partners.list({ customer_rank: 1, limit: 1000 }).catch(() => []);
        let arr = Array.isArray(rows) ? rows : rows?.data && Array.isArray(rows.data) ? rows.data : [];
        if (arr.length) return arr;
        // 2) fallback customer_rank >0 via brute fetch puis filtre local (évite toInt string "true" bug)
        rows = await api.partners.list({ limit: 1000 }).catch(() => []);
        arr = Array.isArray(rows) ? rows : rows?.data && Array.isArray(rows.data) ? rows.data : [];
        // si encore vide, garde tout active
        return arr.filter((p) => p.is_active !== false);
      };
      const [partnerRows, productRows, journalRows] = await Promise.all([
        fetchPartners().catch(() => []),
        api.products.list({ limit: 500 }).catch(() => []),
        api.journals.list({ limit: 100 }).catch(() => []),
      ]);
      if (!active) return;
      const pArr = Array.isArray(partnerRows) ? partnerRows : partnerRows?.data && Array.isArray(partnerRows.data) ? partnerRows.data : [];
      const prArr = Array.isArray(productRows) ? productRows : productRows?.data && Array.isArray(productRows.data) ? productRows.data : [];
      const jArr = Array.isArray(journalRows) ? journalRows : journalRows?.data && Array.isArray(journalRows.data) ? journalRows.data : [];
      console.log("[CustomerInvoice] partners loaded", pArr.length, "products", prArr.length);
      if (!pArr.length) toast("Aucun client trouvé — vérifie API /partners", "error");
      setPartners(pArr);
      setProducts(prArr);
      setJournals(jArr);
    })();
    return () => { active = false; };
  }, []);

  // hydrater form dès que invoice chargé — pour mode édition
  useEffect(() => {
    if (invoice && lines) hydrateFormFromInvoice();
  }, [invoice, lines, hydrateFormFromInvoice]);

  const addLine = () => setForm((c) => ({ ...c, lines: [...c.lines, { product_id: "", name: "", qty: 1, price_unit: "" }] }));
  const updateLine = (idx, patch) => setForm((c) => ({ ...c, lines: c.lines.map((l, i) => i === idx ? { ...l, ...patch } : l) }));
  const removeLine = (idx) => setForm((c) => ({ ...c, lines: c.lines.filter((_, i) => i !== idx) }));
  const chooseProduct = (idx, value) => {
    const p = products.find((x) => String(x.id) === String(value));
    updateLine(idx, { product_id: value, price_unit: p?.list_price || 0, name: p?.name || "" });
  };
  const createInvoice = async () => {
    if (!form.partner_id) { toast("Customer is required", "error"); return; }
    if (!form.lines.some((l) => l.product_id)) { toast("Add at least one product", "error"); return; }
    setCreating(true);
    try {
      const validLines = form.lines.filter((l) => l.product_id);
      // génération facture — cf. guide ci-dessous: out_invoice via account_moves, champs mappés 1:1 avec CustomerInvoiceView
      const body = {
        move_type: "out_invoice",
        partner_id: parseInt(form.partner_id),
        journal_id: form.journal_id ? parseInt(form.journal_id) : undefined,
        date: form.date ? `${form.date}T00:00:00Z` : undefined,
        date_due: form.due_date ? `${form.due_date}T00:00:00Z` : undefined,
        ref: form.ref || undefined,
        code_fournisseur: form.code_fournisseur || undefined,
        billing_period: form.billing_period || undefined,
        maturity_date: form.maturity_date ? `${form.maturity_date}T00:00:00Z` : undefined,
        early_payment_request_sent: !!form.early_payment_request_sent,
        early_payment_request_date: form.early_payment_request_date ? `${form.early_payment_request_date}T00:00:00Z` : undefined,
        buyer: form.buyer || undefined,
        delivery_address: form.delivery_address || undefined,
        sale_order: form.sale_order || undefined,
        payment_reference: form.payment_reference || undefined,
        project_budget: form.project_budget || undefined,
        state: "draft",
        lines: validLines.map((l) => ({ product_id: parseInt(l.product_id), quantity: Number(l.qty) || 1, price_unit: Number(l.price_unit) || 0, name: l.name })),
      };
      const created = await api.accounting.create(body);
      toast(`Invoice ${created?.name || ""} created (draft)`, "success");
      const newId = created?.lewoo_uid || created?.id;
      if (newId) navigate(`/admin-panel/sales/invoices/${newId}`);
      else navigate("/admin-panel/sales/invoices");
    } catch (e) {
      toast(e.message || "Create failed", "error");
    }
    setCreating(false);
  };

  const handleSave = async () => {
    // PUT /account-moves/:id (ROUTES.md:1598) n'accepte que name, move_type, partner_id, date, amount_total, state, journal_id, ref, lines[]
    const supportedBody = {};
    const moveId = invoice.id || invoice.lewoo_uid;
    if (form.partner_id) supportedBody.partner_id = parseInt(form.partner_id);
    if (form.date) supportedBody.date = `${String(form.date).slice(0,10)}T00:00:00Z`;
    if (form.journal_id) supportedBody.journal_id = parseInt(form.journal_id);
    if (form.ref) supportedBody.ref = form.ref;
    // Stage affiché DRAFT mais backend dit non-draft = l'écriture est déjà POSTED (ROUTES.md:1598 lines only if draft)
    // On ne peut pas savoir sans refetch exact : on teste isDraft frontend, mais on gère le 400 en retry sans lines
    const isDraftFrontend = invoice.state === "draft" || invoice.state == null;
    if (isDraftFrontend && form.lines?.length) {
      const valid = form.lines.filter((l) => l.product_id);
      if (valid.length) {
        supportedBody.lines = valid.map((l) => ({
          product_id: parseInt(l.product_id),
          quantity: Number(l.qty) || 1,
          price_unit: Number(l.price_unit) || 0,
          name: l.name || products.find((p) => String(p.id) === String(l.product_id))?.name || "",
        }));
      }
    }
    try {
      if (Object.keys(supportedBody).length) {
        try {
          await api.accounting.update(moveId, supportedBody);
        } catch (e) {
          // Backend 400: Les lignes ne peuvent être modifiées qu'en état brouillon -> on retente sans lines (header seul)
          if (String(e.message).includes("brouillon") && supportedBody.lines) {
            const { lines: _omit, ...headerOnly } = supportedBody;
            console.warn("[Invoice save] retry without lines (already POSTED)", headerOnly);
            if (Object.keys(headerOnly).length) {
              await api.accounting.update(moveId, headerOnly);
              toast("Facture déjà POSTED — lignes verrouillées, seul l'en-tête a été sauvegardé (repasse en DRAFT via Cancel pour éditer les lignes)", "info");
            } else throw e;
            // on a sauvé l'en-tête, on laisse le flux continuer vers le toast/load sans relancer l'erreur
            supportedBody.lines = undefined;
          } else throw e;
        }
      } else {
        toast("Rien à persister en DB (champs custom locaux uniquement)", "info");
      }
      // update local optimistic pour tous les champs Odoo (même custom non persistés DB) — rend l'edit visible immédiatement côté frontend
      setInvoice((prev) => ({
        ...prev,
        partner_id: form.partner_id ? parseInt(form.partner_id) : prev.partner_id,
        partner_name: partners.find((p) => String(p.id) === String(form.partner_id))?.display_name || partners.find((p) => String(p.id) === String(form.partner_id))?.name || prev.partner_name,
        date: form.date ? `${form.date}T00:00:00Z` : prev.date,
        invoice_date: form.date || prev.invoice_date,
        date_due: form.due_date ? `${form.due_date}T00:00:00Z` : prev.date_due,
        invoice_date_due: form.due_date || prev.invoice_date_due,
        journal_id: form.journal_id ? parseInt(form.journal_id) : prev.journal_id,
        ref: form.ref ?? prev.ref,
        partner_ref: form.ref ?? prev.partner_ref,
        code_fournisseur: form.code_fournisseur ?? prev.code_fournisseur,
        billing_period: form.billing_period ?? prev.billing_period,
        maturity_date: form.maturity_date || prev.maturity_date,
        early_payment_request_sent: !!form.early_payment_request_sent,
        early_payment_request_date: form.early_payment_request_date || prev.early_payment_request_date,
        buyer: form.buyer ?? prev.buyer,
        buyer_name: form.buyer ?? prev.buyer_name,
        delivery_address: form.delivery_address ?? prev.delivery_address,
        sale_order: form.sale_order ?? prev.sale_order,
        sale_order_name: form.sale_order ?? prev.sale_order_name,
        payment_reference: form.payment_reference ?? prev.payment_reference,
        project_budget: form.project_budget ?? prev.project_budget,
        salesperson: form.salesperson ?? prev.salesperson,
        sales_team: form.sales_team ?? prev.sales_team,
        recipient_bank: form.recipient_bank ?? prev.recipient_bank,
        incoterm: form.incoterm ?? prev.incoterm,
        fiscal_position: form.fiscal_position ?? prev.fiscal_position,
        auto_post: form.auto_post ?? prev.auto_post,
        to_check: !!form.to_check,
      }));
      if (form.lines?.length) {
        // reflète lignes éditées localement (produit/qty/price)
        const newLines = form.lines.map((l) => ({
          product_id: l.product_id,
          name: l.name,
          product_name: products.find((p) => String(p.id) === String(l.product_id))?.name || l.name,
          quantity: Number(l.qty) || 1,
          qty: Number(l.qty) || 1,
          product_uom_qty: Number(l.qty) || 1,
          price_unit: Number(l.price_unit) || 0,
          price: Number(l.price_unit) || 0,
          price_subtotal: (Number(l.qty) || 1) * (Number(l.price_unit) || 0),
          account_name: "701100 Vente de merchandises dans la Région",
          tax_name: "TVA 19.25% (vente)",
        }));
        setLines(newLines);
      }
      setEditing(false);
      toast(Object.keys(supportedBody).length ? "Invoice saved to DB" : "Invoice updated locally", "success");
      setTimeout(() => load(), 400);
    } catch (e) {
      console.error("[Invoice save] failed", e.message, supportedBody, e);
      toast(e.message || "Save failed — vérifie partner/journal/date/lines", "error");
    }
  };

  const handlePay = async () => {
    if (!invoice) return;
    const amount = Number(invoice.amount_residual || invoice.amount_total || 0);
    if (!amount) { toast("Nothing to pay", "info"); return; }
    const candidates = [invoice.id, invoice.lewoo_uid, invoice.account_move_id].filter(Boolean).map(String);
    const uniq = [...new Set(candidates)];
    let lastErr = null;
    for (const moveId of uniq) {
      try {
        await api.accounting.pay(moveId, { amount });
        toast("Payment registered — Invoice PAID", "success");
        await load();
        return;
      } catch (e) {
        lastErr = e;
        const msg = String(e.message || "").toLowerCase();
        if (msg.includes("invalid value") || msg.includes("non postable") || msg.includes("état actuel")) continue;
        break;
      }
    }
    toast(lastErr?.message || "Payment failed — vérifie l'ID (int attendu)", "error");
  };

  const handleConfirm = async () => {
    if (!invoice) return;
    const debug = () => console.log("[Confirm] invoice raw:", invoice, "lines:", lines, "journals:", journals);
    debug();
    // Pré-validation visible
    if (!lines.length) { toast("Impossible de poster : aucune ligne — ajoute un produit", "error"); return; }
    if (!invoice.journal_id && !journals.length) { toast("Journal manquant — recharge la page", "error"); return; }
    // Détection précoce du bug #990446 (débit 2 ≠ crédit 0): lignes sans compte + journal null
    const hasNullAccount = lines.some(l => l.account_id == null && l.account_code == null);
    if (hasNullAccount) {
      console.warn("[Confirm] lines without account detected", lines);
    }
    // Auto-fix journal manquant ou état null (cause principale du déséquilibre + Failed to fetch si mauvais LEWOO_API_URL)
    const candidates = [invoice.id, invoice.lewoo_uid, invoice.account_move_id].filter(Boolean);
    const uniq = [...new Set(candidates.map(String))];
    if (invoice.journal_id == null || invoice.state == null) {
      try {
        const fixId = uniq[0];
        const patch = {};
        if (!invoice.journal_id) {
          const saleJournal = journals.find(j => j.type === "sale") || journals[0];
          if (saleJournal?.id) patch.journal_id = saleJournal.id; else patch.journal_id = 1;
        }
        if (!invoice.date && !invoice.invoice_date) patch.date = new Date().toISOString().slice(0,10);
        // Si state null/draft avec journal manquant, on le pose avant post
        if (Object.keys(patch).length) {
          console.log("[Confirm] auto-fix patch (journal/date) for unbalanced", fixId, patch);
          await api.accounting.update(fixId, patch).catch(() => {});
          // Ne pas reload tout de suite si on va poster, mais on informe
          if (invoice.journal_id == null) {
            toast("Journal manquant corrigé (Customer Invoices) — re-clique CONFIRM", "info");
            await load();
            return;
          }
        }
      } catch (e) { console.warn("[Confirm] auto-fix failed", e.message); }
    }
    let lastErr = null;
    for (const moveId of uniq) {
      try {
        await api.accounting.post(moveId, {});
        toast("Invoice confirmée → POSTED", "success");
        await load();
        return;
      } catch (e) {
        lastErr = e;
        console.error(`[Confirm] POST /account-moves/${moveId}/post failed:`, e.message, e, "invoice:", invoice);
        const msg = String(e.message || "").toLowerCase();
        if (msg.includes("failed to fetch")) {
          console.error("[Confirm] Network failure — check REACT_APP_LEWOO_API_URL (localhost:8000 vs https://africasystems.com) and backend is running", e);
          toast("Serveur injoignable (Failed to fetch) — vérifie que le backend tourne et REACT_APP_LEWOO_API_URL (http://localhost:8000 en dev, https://africasystems.com en prod)", "error");
          break;
        }
        if (msg.includes("déséquilibrée") || msg.includes("debit") || msg.includes("crédit")) {
          console.table({ id: invoice.id, journal_id: invoice.journal_id, lines: lines.map(l => ({ id: l.id, account_id: l.account_id, debit: l.debit, credit: l.credit, product_id: l.product_id })) });
          toast(`Écriture déséquilibrée (débit ${lines.reduce((s,l)=>s+Number(l.debit||0),0)} ≠ crédit ${lines.reduce((s,l)=>s+Number(l.credit||0),0)}) — Produit sans compte revenu (701100) ou journal null. Fix: édite le produit (catégorie Saleable + compte 701100) ou recrée la facture avec un produit valide, ou contacte backend (sale_order 990443 -> produits 654/655 auto-générés sans compte).`, "error");
          break;
        }
        // Écriture non postable (état null) = mauvais id (sales invoice vs account.move) — on retente l'autre id
        if (msg.includes("non postable") || msg.includes("état actuel") || msg.includes("invalid value")) continue;
        break;
      }
    }
    const detail = lastErr?.message || "Confirm failed";
    if (!String(detail).toLowerCase().includes("déséquilibrée") && !String(detail).toLowerCase().includes("failed to fetch")) {
      console.table({ id: invoice.id, lewoo_uid: invoice.lewoo_uid, account_move_id: invoice.account_move_id, state: invoice.state, payment_state: invoice.payment_state, journal_id: invoice.journal_id, date: invoice.date || invoice.invoice_date, lines: lines.length, linesDetail: lines });
      toast(`${detail}`, "error");
    }
  };

  const handlePreview = async () => {
    if (!invoice) return;
    const w = window.open("", "_blank");
    if (!w) { toast("Popup blocked — allow popups for preview", "error"); return; }
    try {
      const view = mapInvoiceToView(invoice, lines);
      const pdf = await generateInvoicePdf({ invoice: view });
      w.location.href = URL.createObjectURL(pdf);
      toast("Preview ouverte", "success");
    } catch (e) {
      w.close();
      toast(e.message || "Preview failed", "error");
    }
  };

  const handlePrint = async () => {
    if (!invoice) { window.print(); return; }
    try {
      const view = mapInvoiceToView(invoice, lines);
      const blob = await generateInvoicePdf({ invoice: view });
      downloadPdfBlob(blob, `${view.number || "invoice"}.pdf`);
      toast("PDF téléchargé", "success");
    } catch (e) { window.print(); }
  };

  const handleCreditNote = () => {
    if (!invoice) return;
    if (invoice.state === "draft") { toast("Post the invoice before creating a credit note", "info"); return; }
    setCreditForm({ refund_method: "refund", reason: "", journal_id: invoice.journal_id || "", date_mode: "entry", date: new Date().toISOString().slice(0,10) });
    setCreditOpen(true);
  };
  const submitCreditNote = async () => {
    if (!invoice) return;
    const moveId = invoice.lewoo_uid || invoice.id;
    setCreditBusy(true);
    try {
      const payload = {
        refund_method: creditForm.refund_method, // refund | cancel | modify
        reason: creditForm.reason || undefined,
        journal_id: creditForm.journal_id ? parseInt(creditForm.journal_id) : undefined,
        date: creditForm.date_mode === "custom" && creditForm.date ? `${creditForm.date}T00:00:00Z` : undefined,
        date_mode: creditForm.date_mode,
      };
      const res = await api.accounting.creditNote(moveId, payload).catch(async () => {
        return api.accounting.refund({ reversed_entry_id: moveId, partner_id: invoice.partner_id, journal_id: payload.journal_id || invoice.journal_id, reason: payload.reason, refund_method: payload.refund_method, date: payload.date });
      });
      const nid = res?.lewoo_uid || res?.id;
      toast(`Credit Note ${res?.name || ""} created (${creditForm.refund_method})`, "success");
      setCreditOpen(false);
      if (nid) navigate(`/admin-panel/sales/invoices/${nid}`);
      else await load();
    } catch (e) { toast(e.message || "Credit Note failed", "error"); }
    setCreditBusy(false);
  };

  const handleInvoiceStageChange = async (action) => {
    if (!invoice) return;
    const candidates = [invoice.id, invoice.lewoo_uid, invoice.account_move_id].filter(Boolean).map(String);
    const uniq = [...new Set(candidates)];
    let lastErr = null;
    for (const moveId of uniq) {
      try {
        if (action === "post") await api.accounting.post(moveId, {});
        else if (action === "pay") {
          const amount = Number(invoice.amount_residual || invoice.amount_total || 0);
          if (!amount) { toast("Nothing to pay", "info"); return; }
          await api.accounting.pay(moveId, { amount });
        } else if (action === "draft") await api.accounting.cancel(moveId, {});
        toast(action === "post" ? "Invoice → POSTED" : action === "draft" ? "Reset to DRAFT" : "Invoice stage updated", "success");
        await load();
        return;
      } catch (e) {
        lastErr = e;
        console.error(`[Stage ${action}] /account-moves/${moveId} failed:`, e.message, invoice);
        const msg = String(e.message).toLowerCase();
        if (msg.includes("non postable") || msg.includes("invalid value") || msg.includes("état actuel")) continue;
        break;
      }
    }
    toast(lastErr?.message || "Stage change failed", "error");
  };

  const handleOpenSaleOrder = (so) => {
    if (!so) return;
    const soId = so.id || so.lewoo_uid;
    if (soId) navigate(`/admin-panel/sales/${soId}`);
  };

  const handleSend = async () => {
    if (!invoice) return;
    try {
      toast("Génération PDF facture...", "info");
      const view = mapInvoiceToView(invoice, lines);
      let filePath = null;
      let pdfBlob = null;
      try {
        pdfBlob = await generateInvoicePdf({ invoice: view });
        const file = new File([pdfBlob], `${view.number || "invoice"}.pdf`, { type: "application/pdf" });
        const up = await api.upload(file);
        // upload returns { data: { file_path } } ou { file_path }
        filePath = up?.file_path || up?.data?.file_path || up?.filePath || null;
      } catch (e) {
        console.warn("PDF/upload failed, will send without attachment", e);
      }
      if (invoice.sale_order_id) {
        const payload = {};
        if (filePath) {
          payload.attachments = [{ file_path: filePath, file_name: `${view.number}.pdf`, mime_type: "application/pdf" }];
          payload.file_path = filePath; // compat backend
          payload.attachment_ids = [filePath];
        }
        await api.sales.sendEmail(invoice.sale_order_id, payload);
        toast(filePath ? `Email queued with PDF attached (${view.number}.pdf)` : "Email queued (PDF will be generated server-side)", "success");
        return;
      }
      // Fallback standalone invoice: try mail bulk if no sale_order
      if (filePath && invoice.partner_email) {
        try {
          await api.mail.bulk({ to_email: invoice.partner_email, subject: `Invoice ${view.number}`, file_path: filePath, attachments: [{ file_path: filePath }] });
          toast(`Email queued with PDF attached (${view.number}.pdf)`, "success");
          return;
        } catch {}
      }
      // dernier recours: update move sans pièce jointe
      const moveId = invoice.lewoo_uid || invoice.id;
      await api.accounting.update(moveId, filePath ? { attachment_path: filePath } : {});
      toast(filePath ? `Invoice updated with PDF (${view.number}.pdf) — send via mail worker` : "Send & Print — invoice sent", "success");
    } catch (e) { toast(e.message || "Send failed", "error"); }
  };

  if (isNew) {
    return (
      <div style={invStyles.page}>
        <div style={invStyles.topbar}><div style={invStyles.breadcrumb}><button type="button" onClick={() => navigate("/admin-panel/sales/invoices")} style={invStyles.back}>← Invoices</button><span>/</span><strong>New</strong></div><div style={{ display: "flex", alignItems: "center", gap: 12 }}><button type="button" onClick={() => navigate("/admin-panel/sales/invoices")} style={invStyles.cancel}>Back to list</button></div></div>
        <InvoiceForm form={form} setForm={setForm} partners={partners} products={products} journals={journals} subtotal={subtotal} addLine={addLine} updateLine={updateLine} removeLine={removeLine} chooseProduct={chooseProduct} creating={creating} onCreate={createInvoice} onCancel={() => navigate("/admin-panel/sales/invoices")} />
      </div>
    );
  }

  if (loading) return <LoadingScreen />;
  if (!invoice) return <div className="p-8 text-center text-neutral-500">Invoice not found</div>;

  const viewInvoice = mapInvoiceToView(invoice, lines);
  // Source de vérité = viewInvoice (qui gère state null/paid/posted) + raw pour fallback
  const isPosted = viewInvoice.status === "posted" || viewInvoice.status === "paid";
  const isPaid = false;
  const showPay = isPosted && invoice.payment_state !== "paid";
  return (
    <>
      <CustomerInvoiceView
        invoice={viewInvoice}
        editable={editing}
        form={form}
        setForm={setForm}
        partners={partners}
        products={products}
        journals={journals}
        saleOrders={saleOrders}
        onOpenSaleOrder={handleOpenSaleOrder}
        onBack={() => navigate("/admin-panel/sales/invoices")}
        onEdit={() => { hydrateFormFromInvoice(); setEditing(true); }}
        onCancelEdit={() => { setEditing(false); hydrateFormFromInvoice(); }}
        onSave={handleSave}
        onPay={showPay ? handlePay : undefined}
        onConfirm={!isPosted ? handleConfirm : undefined}
      onPreview={handlePreview}
      onPrint={handlePrint}
      onCreditNote={handleCreditNote}
      onNew={() => navigate("/admin-panel/sales/invoices/new")}
        onAddLine={addLine}
        onUpdateLine={updateLine}
        onRemoveLine={removeLine}
        onChooseProduct={chooseProduct}
        onStageChange={handleInvoiceStageChange}
      />
      {creditOpen && (
        <div style={{ position: "fixed", inset: 0, zIndex: 50, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <div style={{ position: "absolute", inset: 0, background: "rgba(0,0,0,.45)" }} onClick={() => !creditBusy && setCreditOpen(false)} />
          <div style={{ position: "relative", background: "#fff", width: "min(720px, 96vw)", borderRadius: 6, boxShadow: "0 20px 60px rgba(0,0,0,.25)", border: "1px solid #dee2e6", display: "flex", flexDirection: "column", maxHeight: "90vh", overflow: "hidden" }}>
            <header style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "14px 18px", borderBottom: "1px solid #dee2e6", cursor: "move" }}>
              <h4 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: "#212529" }}>Credit Note</h4>
              <button type="button" onClick={() => setCreditOpen(false)} disabled={creditBusy} style={{ border: 0, background: "transparent", fontSize: 20, lineHeight: 1, cursor: "pointer", color: "#6c757d" }}>×</button>
            </header>
            <main style={{ padding: "18px", overflowY: "auto", flex: 1 }}>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "18px 24px" }}>
                <div>
                  <label style={{ fontSize: 12, fontWeight: 700, color: "#212529", display: "block", marginBottom: 8 }}>Credit Method <span title="Choose how you want to credit this invoice. You cannot &quot;modify&quot; nor &quot;cancel&quot; if the invoice is already reconciled." style={{ color: "#0dcaf0", cursor: "help" }}>?</span></label>
                  <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                    {[
                      ["refund", "Partial Refund"],
                      ["cancel", "Full Refund"],
                      ["modify", "Full refund and new draft invoice"],
                    ].map(([val, lab]) => (
                      <label key={val} style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, color: "#212529", cursor: "pointer" }}>
                        <input type="radio" name="refund_method" checked={creditForm.refund_method === val} onChange={() => setCreditForm((c) => ({ ...c, refund_method: val }))} />
                        {lab}
                      </label>
                    ))}
                  </div>
                  <div style={{ marginTop: 10, fontSize: 11, color: "#6c757d", background: "#f8f9fa", padding: "8px 10px", borderRadius: 4 }}>The credit note is created in draft and can be edited before being issued.</div>
                  <label style={{ fontSize: 12, fontWeight: 600, color: "#212529", display: "block", marginTop: 14, marginBottom: 6 }}>Reason</label>
                  <input value={creditForm.reason} onChange={(e) => setCreditForm((c) => ({ ...c, reason: e.target.value }))} placeholder="Reason for credit..." style={{ width: "100%", border: 0, borderBottom: "1px solid #aeb8c1", padding: "7px 4px", outline: 0, fontSize: 13 }} />
                  <label style={{ fontSize: 12, fontWeight: 600, color: "#212529", display: "block", marginTop: 14, marginBottom: 6 }}>Reversal Date</label>
                  <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                    <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13 }}><input type="radio" name="date_mode" checked={creditForm.date_mode === "custom"} onChange={() => setCreditForm((c) => ({ ...c, date_mode: "custom" }))} /> Specific</label>
                    <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13 }}><input type="radio" name="date_mode" checked={creditForm.date_mode === "entry"} onChange={() => setCreditForm((c) => ({ ...c, date_mode: "entry" }))} /> Journal Entry Date</label>
                  </div>
                </div>
                <div>
                  <label style={{ fontSize: 12, fontWeight: 600, color: "#212529", display: "block", marginBottom: 6 }}>Use Specific Journal <span title="If empty, uses the journal of the journal entry to be reversed." style={{ color: "#0dcaf0" }}>?</span></label>
                  <select value={creditForm.journal_id} onChange={(e) => setCreditForm((c) => ({ ...c, journal_id: e.target.value }))} style={{ width: "100%", border: 0, borderBottom: "1px solid #aeb8c1", padding: "7px 4px", outline: 0, fontSize: 13, background: "#fff" }}>
                    <option value="">— {invoice.journal || "Default journal"} —</option>
                    {journals.map((j) => <option key={j.id} value={j.id}>{j.name || j.code} — {j.currency || "CFA"}</option>)}
                  </select>
                  <label style={{ fontSize: 12, fontWeight: 600, color: "#212529", display: "block", marginTop: 14, marginBottom: 6 }}>Refund Date</label>
                  <input type="date" value={creditForm.date} onChange={(e) => setCreditForm((c) => ({ ...c, date: e.target.value }))} disabled={creditForm.date_mode !== "custom"} style={{ width: "100%", border: 0, borderBottom: "1px solid #aeb8c1", padding: "7px 4px", outline: 0, fontSize: 13, opacity: creditForm.date_mode !== "custom" ? 0.5 : 1 }} />
                </div>
              </div>
            </main>
            <footer style={{ display: "flex", justifyContent: "flex-start", gap: 8, padding: "12px 18px", borderTop: "1px solid #dee2e6", background: "#fff" }}>
              <button type="button" onClick={submitCreditNote} disabled={creditBusy} style={{ background: "#714b67", color: "#fff", border: 0, padding: "8px 18px", fontSize: 13, fontWeight: 600, cursor: "pointer", opacity: creditBusy ? 0.6 : 1 }}>{creditBusy ? "Reversing..." : "Reverse"}</button>
              <button type="button" onClick={() => setCreditOpen(false)} disabled={creditBusy} style={{ background: "#6c757d", color: "#fff", border: 0, padding: "8px 18px", fontSize: 13, cursor: "pointer" }}>Cancel</button>
            </footer>
          </div>
        </div>
      )}
    </>
  );
};

export default CustomerInvoiceDetailPage;
